import { useAuth } from "@laborelec/lbe-react/auth";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import GestureIcon from "@mui/icons-material/Gesture";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import LockIcon from "@mui/icons-material/Lock";
import SaveIcon from "@mui/icons-material/Save";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { BBox, Kks } from "@shared/types/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { v4 as uuidv4 } from "uuid";
import {
  getComponentHierarchy,
  getPidPdfUrl,
  getSystems,
  renamePid,
  updatePidSubSystem,
} from "../api";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { useKksFilters } from "../hooks/useKksFilters";
import { usePidData } from "../hooks/usePidData";
import { MAX_ZOOM, MIN_ZOOM, useZoom, ZOOM_STEP } from "../hooks/useZoom";
import { MM_TO_PTS, sortKks } from "../utils/kks";
import CommentsDrawer from "./CommentsDrawer";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { KksEditor } from "./KksEditor";
import { KksNavigator } from "./KksNavigator";
import { KksPdfOverlay } from "./KksPdfOverlay";
import { PageNavigator } from "./PageNavigator";
import { PidStatusTooltip } from "./PidStatusTooltip";
import { ZoomControls } from "./ZoomControls";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const INITIAL_ZOOM_PADDING_MM = 50;
const SCROLL_FALLBACK_DELAY_MS = 150;
const MIN_DRAW_SIZE_PX = 10;
const KKS_PANEL_WIDTH = 350;
const PDF_CONTAINER_PADDING_PX = 48;
const PDF_FALLBACK_WIDTH = 800;
const MIN_BBOX_SIZE_MM = 1;

interface Props {
  powerPlantId: string;
  systemId: string;
  pidId: string;
  onBack: () => void;
  onGoToFolder: () => void;
  shortcutsEnabled: boolean;
  onShortcutsToggle: (enabled: boolean) => void;
  isSuperUser?: boolean;
  initialCommentsOpen?: boolean;
  initialKksId?: string | null;
}

export default function PidViewer({
  powerPlantId,
  systemId,
  pidId,
  onBack,
  onGoToFolder,
  shortcutsEnabled,
  onShortcutsToggle,
  isSuperUser = false,
  initialCommentsOpen = false,
  initialKksId = null,
}: Props) {
  const {
    pid,
    isLoading,
    kks,
    setKks,
    dirty,
    setDirty,
    autosaveStatus,
    saveMut,
  } = usePidData(powerPlantId, systemId, pidId);

  const { data: systems = [] } = useQuery({
    queryKey: ["systems", powerPlantId],
    queryFn: () => getSystems(powerPlantId),
  });

  const { data: componentHierarchy } = useQuery({
    queryKey: ["componentHierarchy"],
    queryFn: getComponentHierarchy,
    staleTime: Infinity,
  });

  // Derive component options from the PID's system + sub_system
  const systemType = systems.find((s) => s.id === systemId)?.system_type ?? "";
  const componentOptions = useMemo((): string[] => {
    if (
      !componentHierarchy ||
      !systemType ||
      !pid?.sub_system ||
      pid.sub_system === "Unknown"
    )
      return [];
    return [...(componentHierarchy[systemType]?.[pid.sub_system] ?? [])].sort();
  }, [componentHierarchy, systemType, pid?.sub_system]);

  // Sub-system options for the current system
  const subSystemOptions = useMemo((): string[] => {
    if (!componentHierarchy || !systemType || !componentHierarchy[systemType])
      return [];
    return Object.keys(componentHierarchy[systemType]);
  }, [componentHierarchy, systemType]);

  // Sub-system can no longer be changed once any KKS has a component assigned
  const hasComponentAssigned = useMemo(
    () => kks.some((k) => k.component && k.component !== "Unknown"),
    [kks],
  );
  const subSystemSelected = !!pid?.sub_system && pid.sub_system !== "Unknown";

  const {
    zoom,
    zoomIn,
    zoomOut,
    zoomReset,
    zoomAndRecenter,
    zoomRef,
    scrollToKksRef,
    pendingPageRenderRef,
    selectedIdxRef,
  } = useZoom();

  const queryClient = useQueryClient();

  const subSystemMut = useMutation({
    mutationFn: (sub_system: string) =>
      updatePidSubSystem(powerPlantId, systemId, pidId, sub_system),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pid", pidId] });
      queryClient.invalidateQueries({
        queryKey: ["pids", powerPlantId, systemId],
      });
    },
  });
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [commentsOpen, setCommentsOpen] = useState(initialCommentsOpen);

  const renameMut = useMutation({
    mutationFn: (filename: string) =>
      renamePid(powerPlantId, systemId, pidId, filename),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pids", powerPlantId, systemId],
      });
      setRenameOpen(false);
    },
  });

  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedKksIds, setSelectedKksIds] = useState<Set<string>>(new Set());
  const [pageScale, setPageScale] = useState(1);

  // Multi-selection derived helpers
  const isMultiSelect = selectedKksIds.size > 1;
  // The single "active" KKS (used for editor / navigation) — null in multi-select
  const selectedKksId: string | null =
    selectedKksIds.size === 1
      ? (selectedKksIds.values().next().value ?? null)
      : null;
  // Compat setter — behaves like the old useState setter (single selection)
  const setSelectedKksId = useCallback((id: string | null) => {
    setSelectedKksIds(id ? new Set([id]) : new Set());
  }, []);
  const toggleKksSelection = useCallback((id: string) => {
    setSelectedKksIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Ctrl+drag zone-select state
  const [ctrlSelectStart, setCtrlSelectStart] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [ctrlSelectCurrent, setCtrlSelectCurrent] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [lockedAlertOpen, setLockedAlertOpen] = useState(false);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const kksRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [drawCurrent, setDrawCurrent] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const pdfOriginalWidthRef = useRef(0);
  const pageWrapperRef = useRef<HTMLDivElement>(null);
  const initialFocusDone = useRef(false);

  // Drag-to-pan state (refs only — no re-render needed)
  const isPanningRef = useRef(false);
  const panStartRef = useRef<{
    x: number;
    y: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);

  // Resize state
  const resizingRef = useRef<{
    handleType: string;
    kksId: string;
    startX: number;
    startY: number;
    startBbox: BBox;
  } | null>(null);
  const resizingBboxRef = useRef<BBox | null>(null);
  const [resizingBbox, setResizingBbox] = useState<{
    id: string;
    bbox: BBox;
  } | null>(null);

  // Intercept Ctrl+Scroll in the PDF container to zoom the PDF instead of the browser page
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      const container = pageContainerRef.current;
      if (!container || !container.contains(e.target as Node)) return;
      e.preventDefault();
      const currentZoom = zoomRef.current;
      const newZoom =
        e.deltaY < 0
          ? Math.min(MAX_ZOOM, currentZoom * ZOOM_STEP)
          : Math.max(MIN_ZOOM, currentZoom / ZOOM_STEP);
      zoomAndRecenter(newZoom);
    };
    document.addEventListener("wheel", handleWheel, { passive: false });
    return () => document.removeEventListener("wheel", handleWheel);
  }, [zoomAndRecenter, zoomRef]);

  const { token, isUser } = useAuth();
  const isAdmin = isUser("admin") || isUser("super_user");
  const [removeInk, setRemoveInk] = useState(false);
  const pdfFile = useMemo(
    () => ({
      url: getPidPdfUrl(powerPlantId, systemId, pidId, removeInk),
      httpHeaders: { Authorization: `Bearer ${token}` },
    }),
    [powerPlantId, systemId, pidId, token, removeInk],
  );

  const sorted = useMemo(() => sortKks(kks), [kks]);
  const selectedIdx = useMemo(() => {
    if (selectedKksId === null) return null;
    const idx = sorted.findIndex((k) => k.id === selectedKksId);
    return idx >= 0 ? idx : null;
  }, [sorted, selectedKksId]);
  const pageKks = useMemo(
    () => sorted.filter((b) => b.page === currentPage),
    [sorted, currentPage],
  );

  // Keep refs in sync for stable callbacks
  const sortedRef = useRef(sorted);
  sortedRef.current = sorted;
  const pageScaleRef = useRef(pageScale);
  pageScaleRef.current = pageScale;
  selectedIdxRef.current = selectedIdx;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const saveMutLocalRef = useRef(saveMut);
  saveMutLocalRef.current = saveMut;
  const selectedKksIdsRef = useRef(selectedKksIds);
  selectedKksIdsRef.current = selectedKksIds;
  const isMultiSelectRef = useRef(isMultiSelect);
  isMultiSelectRef.current = isMultiSelect;

  // Scroll PDF container to center a kks item
  const doScrollToKks = useCallback((idx: number, scale: number) => {
    const block = sortedRef.current[idx];
    if (!block?.bbox || !pageContainerRef.current || !pageWrapperRef.current)
      return;
    const bbox = block.bbox;
    const cx = ((bbox.x0 + bbox.x1) / 2) * MM_TO_PTS * scale;
    const cy = ((bbox.y0 + bbox.y1) / 2) * MM_TO_PTS * scale;
    const container = pageContainerRef.current;
    const wrapper = pageWrapperRef.current;
    const cRect = container.getBoundingClientRect();
    const wRect = wrapper.getBoundingClientRect();
    container.scrollTo({
      left:
        wRect.left -
        cRect.left +
        container.scrollLeft +
        cx -
        container.clientWidth / 2,
      top:
        wRect.top -
        cRect.top +
        container.scrollTop +
        cy -
        container.clientHeight / 2,
      behavior: "smooth",
    });
  }, []);

  // When selected block changes, scroll to it in the list
  useEffect(() => {
    if (selectedKksId !== null) {
      const el = kksRefs.current.get(selectedKksId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedKksId]);

  // When selected block changes, switch to its page and scroll to it
  useEffect(() => {
    if (selectedIdx === null) return;
    const block = sorted[selectedIdx];
    if (!block) return;
    if (block.page && block.page !== currentPage) {
      setCurrentPage(block.page);
    }
    scrollToKksRef.current = selectedIdx;
    const timer = setTimeout(() => {
      if (scrollToKksRef.current !== null && !pendingPageRenderRef.current) {
        scrollToKksRef.current = null;
        doScrollToKks(selectedIdx, pageScaleRef.current);
      }
    }, SCROLL_FALLBACK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [selectedKksId, doScrollToKks, scrollToKksRef, pendingPageRenderRef]); // eslint-disable-line -- trigger on id change, not derived idx

  // On initial load: auto-select first kks and compute an initial zoom
  useEffect(() => {
    if (initialFocusDone.current) return;
    if (
      !sorted.length ||
      !pdfOriginalWidthRef.current ||
      !pageContainerRef.current
    )
      return;
    initialFocusDone.current = true;
    const targetId = initialKksId ?? null;
    const item =
      (targetId ? sorted.find((k) => k.id === targetId) : null) ?? sorted[0];
    if (!item?.bbox) {
      setSelectedKksId(item?.id ?? null);
      return;
    }
    const bbox = item.bbox;
    const container = pageContainerRef.current;
    const baseWidth = container.clientWidth - PDF_CONTAINER_PADDING_PX;
    const origWidth = pdfOriginalWidthRef.current;
    const baseScale = baseWidth / origWidth;
    const paddedW = bbox.x1 - bbox.x0 + INITIAL_ZOOM_PADDING_MM * 2;
    const paddedH = bbox.y1 - bbox.y0 + INITIAL_ZOOM_PADDING_MM * 2;
    const zoomW = container.clientWidth / (paddedW * MM_TO_PTS * baseScale);
    const zoomH = container.clientHeight / (paddedH * MM_TO_PTS * baseScale);
    const initZoom = Math.max(
      MIN_ZOOM,
      Math.min(MAX_ZOOM, Math.min(zoomW, zoomH)),
    );
    if (initZoom !== zoomRef.current) {
      pendingPageRenderRef.current = true;
    }
    scrollToKksRef.current = sorted.indexOf(item);
    zoomAndRecenter(initZoom);
    setSelectedKksId(item?.id ?? null);
  }, [
    sorted,
    pageScale,
    zoomRef,
    pendingPageRenderRef,
    scrollToKksRef,
    zoomAndRecenter,
  ]);

  const onDocLoad = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
  }, []);

  const onPageRenderSuccess = useCallback(
    (page: { width: number; originalWidth: number }) => {
      const newScale = page.width / page.originalWidth;
      setPageScale(newScale);
      pdfOriginalWidthRef.current = page.originalWidth;
      pendingPageRenderRef.current = false;
      if (scrollToKksRef.current !== null) {
        const idx = scrollToKksRef.current;
        scrollToKksRef.current = null;
        requestAnimationFrame(() => doScrollToKks(idx, newScale));
      }
    },
    [doScrollToKks, pendingPageRenderRef, scrollToKksRef],
  );

  const updateKksItem = useCallback(
    (id: string, updates: Partial<Kks>) => {
      if (pid?.locked) {
        setLockedAlertOpen(true);
        return;
      }
      setKks((prev) => {
        const next = [...prev];
        const globalIndex = prev.findIndex((b) => b.id === id);
        if (globalIndex >= 0) {
          next[globalIndex] = { ...next[globalIndex], ...updates } as Kks;
        }
        return next;
      });
      setDirty(true);
    },
    [setKks, setDirty, pid?.locked],
  );

  // Stay on current page after a delete; select the first remaining KKS there if any, otherwise deselect.
  const firstIdOnCurrentPage = useCallback((remaining: Kks[]) => {
    const page = currentPageRef.current;
    return remaining.find((k) => k.page === page)?.id ?? null;
  }, []);

  const deleteKksItem = useCallback(
    (id: string) => {
      if (pid?.locked) {
        setLockedAlertOpen(true);
        return;
      }
      const curSorted = sortedRef.current;
      const remaining = curSorted.filter((k) => k.id !== id);
      setKks(remaining);
      setDirty(true);
      setSelectedKksId(firstIdOnCurrentPage(remaining));
    },
    [setKks, setDirty, setSelectedKksId, firstIdOnCurrentPage, pid?.locked],
  );

  // --- Filters & bulk delete ---
  const {
    filterRegex,
    setFilterRegex,
    filterRequired,
    setFilterRequired,
    filterVerified,
    setFilterVerified,
    filterSpof,
    setFilterSpof,
    filterSpofApproved,
    setFilterSpofApproved,
    filterRequiredSpof,
    setFilterRequiredSpof,
    filterComponentSet,
    setFilterComponentSet,
    filterRe,
    filteredIndices,
  } = useKksFilters(sorted);
  const [confirmDeleteIds, setConfirmDeleteIds] = useState<Set<string> | null>(
    null,
  );

  const bulkDeleteFiltered = useCallback(() => {
    if (pid?.locked) {
      setLockedAlertOpen(true);
      return;
    }
    if (!filteredIndices || filteredIndices.length === 0) return;
    const idsToDelete = new Set(
      filteredIndices.flatMap((i) => {
        const id = sorted[i]?.id;
        return id !== undefined ? [id] : [];
      }),
    );
    if (idsToDelete.size > 1) {
      setConfirmDeleteIds(idsToDelete);
    } else {
      const remaining = sorted.filter((b) => !idsToDelete.has(b.id));
      setKks(remaining);
      setSelectedKksId(firstIdOnCurrentPage(remaining));
      setDirty(true);
    }
  }, [
    filteredIndices,
    sorted,
    setKks,
    setDirty,
    firstIdOnCurrentPage,
    pid?.locked,
  ]);

  const confirmBulkDelete = useCallback(() => {
    if (!confirmDeleteIds) return;
    const remaining = sorted.filter((b) => !confirmDeleteIds.has(b.id));
    setKks(remaining);
    const nextId = firstIdOnCurrentPage(remaining);
    setSelectedKksIds(nextId ? new Set([nextId]) : new Set());
    setDirty(true);
    setConfirmDeleteIds(null);
  }, [confirmDeleteIds, sorted, setKks, setDirty, firstIdOnCurrentPage]);

  const navigatePrev = useCallback(() => {
    if (filteredIndices !== null) {
      if (filteredIndices.length === 0) return;
      if (selectedIdx === null) {
        setSelectedKksId(
          sorted[filteredIndices[filteredIndices.length - 1]]?.id ?? null,
        );
      } else {
        const pos = filteredIndices.indexOf(selectedIdx);
        const prevPos = pos <= 0 ? 0 : pos - 1;
        setSelectedKksId(sorted[filteredIndices[prevPos]]?.id ?? null);
      }
    } else {
      if (selectedIdx === null) {
        if (sorted.length > 0)
          setSelectedKksId(sorted[sorted.length - 1]?.id ?? null);
      } else if (selectedIdx > 0) {
        setSelectedKksId(sorted[selectedIdx - 1]?.id ?? null);
      }
    }
  }, [selectedIdx, sorted, filteredIndices]);

  const goToPage = useCallback(
    (page: number) => {
      setCurrentPage(page);
      const firstOnPage = sorted.findIndex((b) => b.page === page);
      setSelectedKksId(
        firstOnPage >= 0 ? (sorted[firstOnPage]?.id ?? null) : null,
      );
    },
    [sorted],
  );

  const navigateNext = useCallback(() => {
    if (filteredIndices !== null) {
      if (filteredIndices.length === 0) return;
      if (selectedIdx === null) {
        setSelectedKksId(sorted[filteredIndices[0]]?.id ?? null);
      } else {
        const pos = filteredIndices.indexOf(selectedIdx);
        const nextPos =
          pos < 0 || pos >= filteredIndices.length - 1
            ? filteredIndices.length - 1
            : pos + 1;
        setSelectedKksId(sorted[filteredIndices[nextPos]]?.id ?? null);
      }
    } else {
      if (selectedIdx === null) {
        if (sorted.length > 0) setSelectedKksId(sorted[0]?.id ?? null);
      } else if (selectedIdx < sorted.length - 1) {
        setSelectedKksId(sorted[selectedIdx + 1]?.id ?? null);
      }
    }
  }, [selectedIdx, sorted, filteredIndices]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcutsEnabled,
    pidLocked: pid?.locked,
    pidStatus: pid?.status,
    isSuperUser,
    navigateNext,
    navigatePrev,
    updateKksItem,
    deleteKksItem,
    setDrawing,
    setConfirmDeleteIds,
    setLockedAlertOpen,
    dirtyRef,
    saveMutRef: saveMutLocalRef,
    selectedIdxRef,
    sortedRef,
    isMultiSelectRef,
    selectedKksIdsRef,
  });

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent, kksId: string, handleType: string, bbox: BBox) => {
      e.stopPropagation();
      e.preventDefault();
      resizingRef.current = {
        handleType,
        kksId,
        startX: e.clientX,
        startY: e.clientY,
        startBbox: { ...bbox },
      };
      resizingBboxRef.current = { ...bbox };
      setResizingBbox({ id: kksId, bbox: { ...bbox } });
    },
    [],
  );

  // Mouse handlers for drawing new blocks
  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (drawing) {
        const rect = e.currentTarget.getBoundingClientRect();
        setDrawStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        setDrawCurrent({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        const rect = e.currentTarget.getBoundingClientRect();
        setCtrlSelectStart({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
        setCtrlSelectCurrent({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    },
    [drawing],
  );

  const handleOverlayMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (resizingRef.current) {
        const { handleType, kksId, startX, startY, startBbox } =
          resizingRef.current;
        const toMm = 1 / (MM_TO_PTS * pageScaleRef.current);
        const dx = (e.clientX - startX) * toMm;
        const dy = (e.clientY - startY) * toMm;
        let { x0, y0, x1, y1 } = startBbox;
        if (handleType.includes("n"))
          y0 = Math.min(startBbox.y0 + dy, startBbox.y1 - MIN_BBOX_SIZE_MM);
        if (handleType.includes("s"))
          y1 = Math.max(startBbox.y1 + dy, startBbox.y0 + MIN_BBOX_SIZE_MM);
        if (handleType.includes("w"))
          x0 = Math.min(startBbox.x0 + dx, startBbox.x1 - MIN_BBOX_SIZE_MM);
        if (handleType.includes("e"))
          x1 = Math.max(startBbox.x1 + dx, startBbox.x0 + MIN_BBOX_SIZE_MM);
        const newBbox = { x0, y0, x1, y1 };
        resizingBboxRef.current = newBbox;
        setResizingBbox({ id: kksId, bbox: newBbox });
        return;
      }
      if (drawStart) {
        const rect = e.currentTarget.getBoundingClientRect();
        setDrawCurrent({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        return;
      }
      if (ctrlSelectStart) {
        const rect = e.currentTarget.getBoundingClientRect();
        setCtrlSelectCurrent({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    },
    [drawStart, ctrlSelectStart],
  );

  const handleOverlayMouseUp = useCallback(() => {
    if (resizingRef.current) {
      const id = resizingRef.current.kksId;
      const newBbox = resizingBboxRef.current;
      resizingRef.current = null;
      resizingBboxRef.current = null;
      setResizingBbox(null);
      if (newBbox) updateKksItem(id, { bbox: newBbox });
      return;
    }

    // Ctrl+drag zone selection
    if (ctrlSelectStart && ctrlSelectCurrent) {
      const selW = Math.abs(ctrlSelectCurrent.x - ctrlSelectStart.x);
      const selH = Math.abs(ctrlSelectCurrent.y - ctrlSelectStart.y);
      // Only perform zone-select when the drag is large enough; below the
      // threshold it is treated as a plain click and the KKS box's own
      // onClick handler (toggleKksSelection) takes care of it.
      if (selW > MIN_DRAW_SIZE_PX || selH > MIN_DRAW_SIZE_PX) {
        const selX0 = Math.min(ctrlSelectStart.x, ctrlSelectCurrent.x);
        const selY0 = Math.min(ctrlSelectStart.y, ctrlSelectCurrent.y);
        const selX1 = selX0 + selW;
        const selY1 = selY0 + selH;
        const scale = pageScaleRef.current;
        const newIds = new Set<string>();
        sortedRef.current.forEach((item) => {
          if (!item.bbox || item.page !== currentPage) return;
          const left = item.bbox.x0 * MM_TO_PTS * scale;
          const top = item.bbox.y0 * MM_TO_PTS * scale;
          const right = item.bbox.x1 * MM_TO_PTS * scale;
          const bottom = item.bbox.y1 * MM_TO_PTS * scale;
          if (right > selX0 && left < selX1 && bottom > selY0 && top < selY1) {
            newIds.add(item.id);
          }
        });
        if (newIds.size > 0) setSelectedKksIds(newIds);
      }
      setCtrlSelectStart(null);
      setCtrlSelectCurrent(null);
      return;
    }
    if (!drawStart || !drawCurrent) return;

    const x0px = Math.min(drawStart.x, drawCurrent.x);
    const y0px = Math.min(drawStart.y, drawCurrent.y);
    const x1px = Math.max(drawStart.x, drawCurrent.x);
    const y1px = Math.max(drawStart.y, drawCurrent.y);

    if (x1px - x0px > MIN_DRAW_SIZE_PX && y1px - y0px > MIN_DRAW_SIZE_PX) {
      if (pid?.locked) {
        setLockedAlertOpen(true);
      } else {
        const toMm = 1 / (MM_TO_PTS * pageScale);
        const newKks: Kks = {
          id: uuidv4(),
          text: "",
          confidence: null,
          horizontal: true,
          page: currentPage,
          bbox: {
            x0: x0px * toMm,
            y0: y0px * toMm,
            x1: x1px * toMm,
            y1: y1px * toMm,
          },
          required: true,
          verified: false,
          spof: false,
          required_spof: false,
          spof_approved: false,
          impact: 1,
          component: "Unknown",
          stamp_on_pdf: false,
        };
        setKks((prev) => {
          const newList = sortKks([...prev, newKks]);
          setTimeout(() => setSelectedKksId(newKks.id), 0);
          return newList;
        });
        setDirty(true);
      }
    }

    setDrawStart(null);
    setDrawCurrent(null);
    setDrawing(false);
  }, [
    drawStart,
    drawCurrent,
    ctrlSelectStart,
    ctrlSelectCurrent,
    currentPage,
    pageScale,
    updateKksItem,
    setKks,
    setDirty,
    pid?.locked,
  ]);

  // Pan handlers
  const handlePanMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (drawing) return;
      // Only primary button
      if (e.button !== 0) return;
      // Ctrl is used for multi-select / zone-select — don't pan
      if (e.ctrlKey || e.metaKey) return;
      const container = pageContainerRef.current;
      if (!container) return;
      isPanningRef.current = true;
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
      };
      container.style.cursor = "grabbing";
    },
    [drawing],
  );

  const handlePanMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPanningRef.current || !panStartRef.current) return;
      const container = pageContainerRef.current;
      if (!container) return;
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      container.scrollLeft = panStartRef.current.scrollLeft - dx;
      container.scrollTop = panStartRef.current.scrollTop - dy;
    },
    [],
  );

  const handlePanEnd = useCallback(() => {
    if (resizingRef.current) {
      const id = resizingRef.current.kksId;
      const newBbox = resizingBboxRef.current;
      resizingRef.current = null;
      resizingBboxRef.current = null;
      setResizingBbox(null);
      if (newBbox) updateKksItem(id, { bbox: newBbox });
    }
    // Cancel any in-progress ctrl+drag zone selection
    setCtrlSelectStart(null);
    setCtrlSelectCurrent(null);
    if (!isPanningRef.current) return;
    isPanningRef.current = false;
    panStartRef.current = null;
    const container = pageContainerRef.current;
    if (container) container.style.cursor = drawing ? "crosshair" : "grab";
  }, [drawing, updateKksItem]);

  const drawRect =
    drawStart && drawCurrent
      ? {
          left: Math.min(drawStart.x, drawCurrent.x),
          top: Math.min(drawStart.y, drawCurrent.y),
          width: Math.abs(drawCurrent.x - drawStart.x),
          height: Math.abs(drawCurrent.y - drawStart.y),
        }
      : null;

  const selectedKks = selectedIdx !== null ? sorted[selectedIdx] : null;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "#F5F7FA",
        }}
      >
        <CircularProgress sx={{ color: "#009DE0" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: "#F5F7FA",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          bgcolor: "#fff",
          borderBottom: "1px solid #DEE3EA",
        }}
      >
        <Tooltip title="Back to where you came from">
          <IconButton
            onClick={() => {
              if (dirty && !saveMut.isPending) {
                saveMut.mutate(undefined, { onSettled: onBack });
              } else {
                onBack();
              }
            }}
            sx={{
              color: "#5C6B7A",
              "&:hover": { color: "#002A5C", bgcolor: "#EDF1F7" },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Go to containing folder">
          <IconButton
            onClick={() => {
              if (dirty && !saveMut.isPending) {
                saveMut.mutate(undefined, { onSettled: onGoToFolder });
              } else {
                onGoToFolder();
              }
            }}
            sx={{
              color: "#5C6B7A",
              "&:hover": { color: "#002A5C", bgcolor: "#EDF1F7" },
            }}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography
          variant="subtitle2"
          noWrap
          sx={{ color: "#002A5C", fontWeight: 600, maxWidth: 220 }}
        >
          {pid?.filename}
        </Typography>
        {isAdmin && (
          <Tooltip title="Rename PID">
            <IconButton
              size="small"
              onClick={() => {
                setRenameName(pid?.filename ?? "");
                setRenameOpen(true);
              }}
              sx={{ color: "#8E99A8", "&:hover": { color: "#009DE0" } }}
            >
              <DriveFileRenameOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {pid?.locked && (
          <Tooltip title="This PID is locked — only an admin can unlock it">
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                px: "8px",
                py: "2px",
                borderRadius: "999px",
                bgcolor: "#FFEBEE",
                color: "#E30613",
                fontSize: "0.7rem",
                fontWeight: 600,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <LockIcon sx={{ fontSize: 10, flexShrink: 0 }} />
              Locked
            </Box>
          </Tooltip>
        )}
        {pid?.status && <PidStatusTooltip status={pid.status} kks={kks} />}
        <Box sx={{ flex: 1 }} />

        {/* Page nav */}
        <ZoomControls
          zoom={zoom}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onZoomReset={zoomReset}
          onZoomAndRecenter={zoomAndRecenter}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <PageNavigator
          currentPage={currentPage}
          numPages={numPages}
          onGoToPage={goToPage}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* KKS nav */}
        <KksNavigator
          selectedIdx={selectedIdx}
          total={sorted.length}
          onPrev={navigatePrev}
          onNext={navigateNext}
          onJump={(n) => {
            const clamped = Math.max(0, Math.min(sorted.length - 1, n - 1));
            setSelectedKksId(sorted[clamped]?.id ?? null);
          }}
        />

        <Box sx={{ flex: 1 }} />
        {/* Actions */}
        <Tooltip
          title={
            pid?.locked
              ? "PID is locked — only an admin can unlock it"
              : "Draw a new KKS on the PDF"
          }
        >
          <span>
            <Button
              variant={drawing ? "contained" : "outlined"}
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                if (pid?.locked) {
                  setLockedAlertOpen(true);
                  return;
                }
                setDrawing(!drawing);
              }}
              disabled={!!pid?.locked}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: 2,
                ...(drawing
                  ? {
                      bgcolor: "#009DE0",
                      color: "#fff",
                      "&:hover": { bgcolor: "#0087C3" },
                      border: "none",
                    }
                  : {
                      borderColor: "#C4CDD8",
                      color: "#5C6B7A",
                      "&:hover": {
                        borderColor: "#009DE0",
                        color: "#009DE0",
                        bgcolor: "rgba(0,157,224,0.06)",
                      },
                    }),
              }}
            >
              New KKS
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            shortcutsEnabled ? (
              <Box
                component="ul"
                sx={{ m: 0, pl: 2, py: 0.5, listStyle: "disc" }}
              >
                <li>↑ ← / ↓ → — Navigate KKS</li>
                <li>1 2 3 4 — Set impact level</li>
                <li>r — Required</li>
                <li>v — Verified</li>
                <li>s — SPOF</li>
                <li>a — SPOF Approved (only when SPOF)</li>
                <li>q — Required SPOF (only when SPOF)</li>
                <li>n — New KKS</li>
                <li>Del — Delete</li>
                <li>Ctrl+S — Save</li>
              </Box>
            ) : (
              "Enable keyboard shortcuts"
            )
          }
        >
          <Button
            variant={shortcutsEnabled ? "contained" : "outlined"}
            size="small"
            startIcon={<KeyboardIcon />}
            onClick={() => onShortcutsToggle(!shortcutsEnabled)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              borderRadius: 2,
              ...(shortcutsEnabled
                ? {
                    bgcolor: "#009DE0",
                    color: "#fff",
                    "&:hover": { bgcolor: "#0087C3" },
                    border: "none",
                  }
                : {
                    borderColor: "#C4CDD8",
                    color: "#5C6B7A",
                    "&:hover": {
                      borderColor: "#009DE0",
                      color: "#009DE0",
                      bgcolor: "rgba(0,157,224,0.06)",
                    },
                  }),
            }}
          >
            {shortcutsEnabled ? "Shortcuts ON" : "Shortcuts"}
          </Button>
        </Tooltip>

        <Tooltip
          title={
            removeInk
              ? "Show the hand-drawn markings of the original PDF"
              : "Hide the hand-drawn markings of the original PDF"
          }
        >
          <Button
            variant={removeInk ? "contained" : "outlined"}
            size="small"
            startIcon={<GestureIcon />}
            onClick={() => setRemoveInk(!removeInk)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              borderRadius: 2,
              ...(removeInk
                ? {
                    bgcolor: "#009DE0",
                    color: "#fff",
                    "&:hover": { bgcolor: "#0087C3" },
                    border: "none",
                  }
                : {
                    borderColor: "#C4CDD8",
                    color: "#5C6B7A",
                    "&:hover": {
                      borderColor: "#009DE0",
                      color: "#009DE0",
                      bgcolor: "rgba(0,157,224,0.06)",
                    },
                  }),
            }}
          >
            {removeInk ? "Markings removed" : "Remove markings"}
          </Button>
        </Tooltip>

        <Tooltip title={commentsOpen ? "Hide comments" : "Show comments"}>
          <Button
            variant={commentsOpen ? "contained" : "outlined"}
            size="small"
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={() => setCommentsOpen(!commentsOpen)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              borderRadius: 2,
              ...(commentsOpen
                ? {
                    bgcolor: "#009DE0",
                    color: "#fff",
                    "&:hover": { bgcolor: "#0087C3" },
                    border: "none",
                  }
                : {
                    borderColor: "#C4CDD8",
                    color: "#5C6B7A",
                    "&:hover": {
                      borderColor: "#009DE0",
                      color: "#009DE0",
                      bgcolor: "rgba(0,157,224,0.06)",
                    },
                  }),
            }}
          >
            Comments
          </Button>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Save */}
        <Tooltip
          title={
            pid?.locked
              ? "PID is locked — only an admin can unlock it"
              : undefined
          }
        >
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              disabled={!dirty || saveMut.isPending || !!pid?.locked}
              onClick={() => {
                if (pid?.locked) {
                  setLockedAlertOpen(true);
                  return;
                }
                saveMut.mutate();
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: 2,
                bgcolor: "#002A5C",
                "&:hover": { bgcolor: "#001F45" },
                "&.Mui-disabled": { bgcolor: "#DEE3EA", color: "#8E99A8" },
              }}
            >
              Save
            </Button>
          </span>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            minWidth: 80,
            textAlign: "center",
            fontWeight: 500,
            color:
              autosaveStatus === "saved"
                ? "success.main"
                : autosaveStatus === "error"
                  ? "error.main"
                  : "transparent",
          }}
        >
          {autosaveStatus === "saved"
            ? "Autosaved"
            : autosaveStatus === "error"
              ? "Failed"
              : "\u00A0"}
        </Typography>
      </Box>

      {/* Main area: PDF + Editor */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* PDF viewer with overlays */}
        <Box
          ref={pageContainerRef}
          sx={{
            flex: 1,
            overflow: "auto",
            position: "relative",
            bgcolor: "#D0D6E0",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            p: 2,
            cursor: drawing ? "crosshair" : "grab",
            userSelect: "none",
          }}
          onMouseDown={handlePanMouseDown}
          onMouseMove={handlePanMouseMove}
          onMouseUp={handlePanEnd}
          onMouseLeave={handlePanEnd}
        >
          <Box
            ref={pageWrapperRef}
            sx={{
              position: "relative",
              display: "inline-block",
              margin: "0 auto",
              ...(drawing && {
                "& .react-pdf__Page__textContent": { pointerEvents: "none" },
                "& .react-pdf__Page__annotations": { pointerEvents: "none" },
              }),
            }}
          >
            <Document file={pdfFile} onLoadSuccess={onDocLoad}>
              <Page
                pageNumber={currentPage}
                width={
                  (pageContainerRef.current
                    ? pageContainerRef.current.clientWidth -
                      PDF_CONTAINER_PADDING_PX
                    : PDF_FALLBACK_WIDTH) * zoom
                }
                onRenderSuccess={onPageRenderSuccess}
              />
            </Document>
            {/* Overlay for blocks */}
            <KksPdfOverlay
              kks={pageKks}
              selectedIds={selectedKksIds}
              resizingBbox={resizingBbox}
              drawing={drawing}
              drawRect={drawRect}
              ctrlSelectStart={ctrlSelectStart}
              ctrlSelectCurrent={ctrlSelectCurrent}
              pageScale={pageScale}
              pidLocked={!!pid?.locked}
              isMultiSelect={isMultiSelect}
              onSelect={(id, ctrl) =>
                ctrl ? toggleKksSelection(id) : setSelectedKksId(id)
              }
              onResizeStart={handleResizeMouseDown}
              onMouseDown={handleOverlayMouseDown}
              onMouseMove={handleOverlayMouseMove}
              onMouseUp={handleOverlayMouseUp}
            />
          </Box>
        </Box>

        {/* KKS editor panel */}
        <Box
          sx={{
            width: KKS_PANEL_WIDTH,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: "#fff",
            borderLeft: "1px solid #DEE3EA",
          }}
        >
          {/* Sub-system selector (applies to the whole PID, not a single KKS) */}
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #DEE3EA",
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "#5C6B7A",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
              }}
            >
              Sub-system
            </Typography>
            <Tooltip
              title={
                hasComponentAssigned
                  ? "Sub-system cannot be changed once components have been assigned to KKS items"
                  : ""
              }
              placement="top"
            >
              <span>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={subSystemOptions}
                  value={
                    pid?.sub_system && pid.sub_system !== "Unknown"
                      ? pid.sub_system
                      : null
                  }
                  disabled={hasComponentAssigned || !!pid?.locked}
                  onChange={(_e, v) => {
                    if (pid?.locked) {
                      setLockedAlertOpen(true);
                      return;
                    }
                    const newVal = v ?? "Unknown";
                    if (dirty) {
                      saveMut.mutate(undefined, {
                        onSettled: () => subSystemMut.mutate(newVal),
                      });
                    } else {
                      subSystemMut.mutate(newVal);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select sub-system…"
                      sx={{
                        mt: 0.5,
                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                      }}
                    />
                  )}
                  disableClearable={false}
                />
              </span>
            </Tooltip>
          </Box>

          {/* Selected KKS editor */}
          <Box
            sx={{
              flexShrink: 0,
              overflow: "auto",
              maxHeight: "45%",
              p: 2,
              borderBottom: "1px solid #DEE3EA",
            }}
          >
            {isMultiSelect ? (
              /* Multi-selection panel — only delete is allowed */
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                  py: 2,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: "#5C6B7A",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                  }}
                >
                  {selectedKksIds.size} KKS selected
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#8E99A8", textAlign: "center" }}
                >
                  Hold Ctrl and click to add/remove items, or Ctrl+drag to
                  select a zone.
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={!!pid?.locked}
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      if (pid?.locked) {
                        setLockedAlertOpen(true);
                        return;
                      }
                      setConfirmDeleteIds(new Set(selectedKksIds));
                    }}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      borderRadius: 2,
                    }}
                  >
                    Delete {selectedKksIds.size} KKS
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedKksIds(new Set())}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      borderRadius: 2,
                      borderColor: "#C4CDD8",
                      color: "#5C6B7A",
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            ) : selectedKks ? (
              <KksEditor
                kks={selectedKks}
                index={selectedIdx!}
                total={sorted.length}
                onChange={(updates) => updateKksItem(selectedKksId!, updates)}
                onDelete={() => deleteKksItem(selectedKksId!)}
                shortcutsEnabled={shortcutsEnabled}
                componentOptions={componentOptions}
                componentDisabled={!subSystemSelected}
                locked={!!pid?.locked}
                frozenRequiredSpof={
                  !isSuperUser &&
                  !!selectedKks.required_spof &&
                  pid?.status !== "required_spof_review"
                }
                disableRequiredSpof={
                  !isSuperUser &&
                  pid?.status !== undefined &&
                  pid.status !== "required_spof_review" &&
                  pid.status !== "processing" &&
                  pid.status !== "error"
                }
              />
            ) : (
              <Box sx={{ py: 3, textAlign: "center", color: "#8E99A8" }}>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  Click a KKS on the PDF or use ↑↓ to navigate.
                  <br />
                  Hold Ctrl and click to select multiple.
                </Typography>
              </Box>
            )}
          </Box>

          {/* KKS list header */}
          <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
            <Typography
              variant="overline"
              sx={{
                color: "#5C6B7A",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
              }}
            >
              All KKS ({sorted.length})
            </Typography>
          </Box>

          {/* Filters */}
          <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Filter by regex…"
              value={filterRegex}
              onChange={(e) => setFilterRegex(e.target.value)}
              error={filterRegex !== "" && filterRe === null}
              helperText={
                filterRegex !== "" && filterRe === null
                  ? "Invalid regex"
                  : undefined
              }
              sx={{ mb: 0.75 }}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
              {(
                [
                  {
                    label: "Required",
                    value: filterRequired,
                    set: setFilterRequired,
                  },
                  {
                    label: "Verified",
                    value: filterVerified,
                    set: setFilterVerified,
                  },
                  { label: "SPOF", value: filterSpof, set: setFilterSpof },
                  {
                    label: "SPOF Approved",
                    value: filterSpofApproved,
                    set: setFilterSpofApproved,
                  },
                  {
                    label: "Req. SPOF",
                    value: filterRequiredSpof,
                    set: setFilterRequiredSpof,
                  },
                  {
                    label: "Component",
                    value: filterComponentSet,
                    set: setFilterComponentSet,
                  },
                ] as const
              ).map(({ label, value, set }) => (
                <Box
                  key={label}
                  component="span"
                  onClick={() =>
                    set((v) => (v === null ? true : v === true ? false : null))
                  }
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    px: "8px",
                    py: "2px",
                    borderRadius: "999px",
                    cursor: "pointer",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    border: "1px solid",
                    userSelect: "none",
                    ...(value === null
                      ? {
                          borderColor: "#C4CDD8",
                          color: "#5C6B7A",
                          bgcolor: "transparent",
                          "&:hover": { bgcolor: "#EDF1F7" },
                        }
                      : value === true
                        ? {
                            borderColor: "#009DE0",
                            color: "#009DE0",
                            bgcolor: "#E8F4FD",
                            "&:hover": { bgcolor: "#D6EDFB" },
                          }
                        : {
                            borderColor: "#E30613",
                            color: "#E30613",
                            bgcolor: "#FFF5F5",
                            "&:hover": { bgcolor: "#FFE8E8" },
                          }),
                  }}
                >
                  {value === null ? label : value ? `${label} ✓` : `${label} ✗`}
                </Box>
              ))}
            </Box>
            {filteredIndices !== null && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ flex: 1, color: "#5C6B7A" }}
                >
                  {filteredIndices.length} / {sorted.length} match
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={filteredIndices.length === 0 || !!pid?.locked}
                  onClick={bulkDeleteFiltered}
                  startIcon={<DeleteIcon />}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.72rem",
                    borderRadius: 2,
                  }}
                >
                  Delete matches
                </Button>
              </Box>
            )}
          </Box>

          <Divider sx={{ flexShrink: 0 }} />

          {/* KKS list */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", py: 0.5 }}>
            {sorted.map((item, idx) => {
              const hidden =
                filteredIndices !== null && !filteredIndices.includes(idx);
              if (hidden) return null;
              const isSelected = selectedKksIds.has(item.id);
              return (
                <Box
                  key={item.id}
                  ref={(el: HTMLDivElement | null) => {
                    if (el) kksRefs.current.set(item.id, el);
                    else kksRefs.current.delete(item.id);
                  }}
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                      toggleKksSelection(item.id);
                    } else {
                      setSelectedKksId(item.id);
                    }
                  }}
                  sx={{
                    px: 2,
                    py: 0.75,
                    mx: 0.5,
                    mb: 0.25,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    fontSize: "0.78rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: isSelected ? "rgba(0,157,224,0.1)" : "transparent",
                    color: isSelected ? "#009DE0" : "#444",
                    fontWeight: isSelected ? 600 : 400,
                    "&:hover": {
                      bgcolor: isSelected ? "rgba(0,157,224,0.15)" : "#EDF1F7",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: isSelected ? "#009DE0" : "#B0BAC5",
                      minWidth: 24,
                      flexShrink: 0,
                    }}
                  >
                    p{item.page}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.text || <em style={{ color: "#bbb" }}>empty</em>}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Comments drawer */}
        <CommentsDrawer
          powerPlantId={powerPlantId}
          systemId={systemId}
          pidId={pidId}
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
        />
      </Box>

      {/* Bulk delete confirmation dialog */}
      <ConfirmDeleteDialog
        open={confirmDeleteIds !== null}
        title="Delete KKS"
        message={
          <>
            Are you sure you want to delete{" "}
            <strong>{confirmDeleteIds?.size ?? 0}</strong> out of{" "}
            <strong>{sorted.length}</strong> KKS items?
          </>
        }
        onClose={() => setConfirmDeleteIds(null)}
        onConfirm={confirmBulkDelete}
      />

      {/* Rename dialog */}
      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename PID</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Filename"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && renameName.trim()) {
                renameMut.mutate(renameName.trim());
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!renameName.trim() || renameMut.isPending}
            onClick={() => renameMut.mutate(renameName.trim())}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Locked alert dialog */}
      <Dialog
        open={lockedAlertOpen}
        onClose={() => setLockedAlertOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockIcon sx={{ color: "#E30613", fontSize: 20 }} />
          PID is locked
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This PID has been locked by an admin and can no longer be edited.
            Only an admin can unlock it. You can still view the file and its KKS
            items, but saving any changes is disabled.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setLockedAlertOpen(false)}
            sx={{
              bgcolor: "#002A5C",
              "&:hover": { bgcolor: "#001F45" },
              textTransform: "none",
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
